'use client';

import Link from "next/link";
import { useMemo, useState } from "react";
import {
  ArrowUpRight,
  ChevronDown,
  ExternalLink,
  Sheet,
  Phone,
  MapPin,
  Mail,
  User,
  Wrench,
  Car
} from "lucide-react";
import type { ClientReport, ClientReportVehicle } from "@/types/reports";
import {
  formatCpfCnpj,
  formatCurrency,
  formatDateTime,
  formatPhone,
  formatVehiclePlate,
} from "@/lib/formatters";
import { WorkOrderStatus } from "@/types/work-order-status";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const workOrderStatusLabels: Record<WorkOrderStatus, string> = {
  [WorkOrderStatus.Open]: "Aberta",
  [WorkOrderStatus.InProgress]: "Em andamento",
  [WorkOrderStatus.AwaitingParts]: "Aguardando peças",
  [WorkOrderStatus.Ready]: "Pronta",
  [WorkOrderStatus.Delivered]: "Entregue",
};

const formatWorkOrderStatus = (status: WorkOrderStatus): string =>
  workOrderStatusLabels[status] ?? status;

type ClientReportCardProps = {
  report: ClientReport;
  startOpen?: boolean;
  showDetailsLink?: boolean;
  onOpenVehicle?: (vehicleId: string) => void;
  onOpenWorkOrder?: (workOrderId: string) => void;
};

export function ClientReportCard({
  report,
  startOpen = false,
  showDetailsLink = true,
  onOpenVehicle,
  onOpenWorkOrder,
}: ClientReportCardProps) {
  const [open, setOpen] = useState(startOpen);

  // Calculate executed vs quoted percentage for the whole client
  const investment = useMemo(() => {
    const quoted = report.totals?.quoteTotal ?? 0;
    const executed = report.totals?.workOrderTotal ?? 0;
    const total = quoted + executed;
    return {
      quoted,
      executed,
      total,
      quotedPercent: total > 0 ? (quoted / total) * 100 : 0,
      executedPercent: total > 0 ? (executed / total) * 100 : 0,
    };
  }, [report.totals]);

  return (
    <div className="group overflow-hidden rounded-[10px] border border-[#e5e7eb] bg-white shadow-sm transition-all hover:border-[#E8441A] hover:shadow-md dark:border-slate-800 dark:bg-slate-900/40 dark:hover:border-[#E8441A]/50">
      <div
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen((previous) => !previous);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${open ? "Recolher" : "Expandir"} detalhes de ${report.name}`}
        className="cursor-pointer bg-white p-4 transition-colors hover:bg-slate-50/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8441A] focus-visible:ring-inset dark:bg-slate-900/40 dark:hover:bg-slate-800/30"
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          {/* Header Main Info */}
          <div className="flex items-start gap-4">
            <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-600 font-bold text-white shadow-sm dark:bg-indigo-500">
              <User className="h-6 w-6" />
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  {report.name}
                </h3>
                <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {report.vehicles?.length ?? 0} Veículos
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  {formatCpfCnpj(report.document)}
                </span>
                {(report.email || report.phone || report.mobile) && (
                  <>
                    <span className="text-slate-300">•</span>
                    <span className="flex items-center gap-1">
                      {report.email ? <Mail className="h-3 w-3" /> : <Phone className="h-3 w-3" />}
                      {report.email ?? formatPhone(report.mobile ?? report.phone ?? "")}
                    </span>
                  </>
                )}
              </div>

              {report.city && (
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  <MapPin className="h-3 w-3" />
                  {report.city} {report.state ? `/ ${report.state}` : ''}
                </div>
              )}
            </div>
          </div>

          {/* Key Metrics */}
          <div className="flex items-center gap-6 md:gap-8">
            <div className="text-right">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Total Investido</p>
              <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(report.totals?.receivedTotal ?? 0)}
              </p>
            </div>
            <div className="text-right hidden sm:block">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">A Receber</p>
              <p className="text-lg font-bold text-slate-700 dark:text-slate-300">
                {formatCurrency(report.totals?.pendingReceivableTotal ?? 0)}
              </p>
            </div>

            <div className="flex flex-col items-end gap-2">
              {showDetailsLink && (
                <Link
                  href={`/relatorios/clientes/${report.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold text-slate-600 transition hover:border-[#E8441A] hover:text-[#E8441A] dark:border-slate-800 dark:bg-slate-900/50 dark:text-slate-300"
                >
                  Ver Detalhes <ExternalLink className="h-3 w-3" />
                </Link>
              )}
              <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </div>
          </div>
        </div>

        {/* Mini Progress Bar */}
        <div className="mt-4 flex h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div className="bg-emerald-500" style={{ width: `${investment.executedPercent}%` }} />
          <div className="bg-[rgba(232,68,26,0.08)]" style={{ width: `${investment.quotedPercent}%` }} />
        </div>
        <div className="mt-1 flex justify-between text-[10px] font-medium text-slate-400">
          <span>Executado: {investment.executedPercent.toFixed(0)}%</span>
          <span>Orçado: {investment.quotedPercent.toFixed(0)}%</span>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-100 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-900/20">
          <Tabs defaultValue="vehicles" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:w-[300px]">
              <TabsTrigger value="vehicles">Veículos ({report.vehicles?.length ?? 0})</TabsTrigger>
              <TabsTrigger value="overview">Visão Financeira</TabsTrigger>
            </TabsList>

            {/* TAB: VEÍCULOS */}
            <TabsContent value="vehicles" className="mt-4 space-y-3">
              {Array.isArray(report.vehicles) && report.vehicles.length ? (
                report.vehicles.map(vehicle => (
                  <VehicleBlock
                    key={vehicle.id}
                    vehicle={vehicle}
                    onOpenVehicle={onOpenVehicle}
                    onOpenWorkOrder={onOpenWorkOrder}
                  />
                ))
              ) : (
                <p className="text-center text-sm text-slate-500 py-8">
                  Nenhum veículo cadastrado para este cliente.
                </p>
              )}
            </TabsContent>

            {/* TAB: FINANCEIRO */}
            <TabsContent value="overview" className="mt-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-xl border border-[rgba(232,68,26,0.1)] bg-[rgba(232,68,26,0.06)]/50 p-4 dark:border-[rgba(232,68,26,0.15)] dark:bg-[rgba(232,68,26,0.08)]/10">
                  <div className="mb-1 flex items-center gap-2 text-[#E8441A] dark:text-[#E8441A]">
                    <Sheet className="h-4 w-4" />
                    <h4 className="text-xs font-bold uppercase tracking-wide">Total Orçado</h4>
                  </div>
                  <p className="text-2xl font-bold text-[#d63b15] dark:text-[#E8441A]">
                    {formatCurrency(report.totals?.quoteTotal ?? 0)}
                  </p>
                  <p className="text-xs text-[#E8441A]/70 dark:text-[#E8441A]/70">
                    {report.totals?.quoteCount ?? 0} orçamentos gerados
                  </p>
                </div>

                <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 dark:border-emerald-900/30 dark:bg-emerald-900/10">
                  <div className="mb-1 flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                    <Wrench className="h-4 w-4" />
                    <h4 className="text-xs font-bold uppercase tracking-wide">Total Executado</h4>
                  </div>
                  <p className="text-2xl font-bold text-emerald-800 dark:text-emerald-200">
                    {formatCurrency(report.totals?.workOrderTotal ?? 0)}
                  </p>
                  <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70">
                    {report.totals?.workOrderCount ?? 0} ordens de serviço
                  </p>
                </div>

                <div className="rounded-[10px] border border-[#e5e7eb] bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                  <div className="mb-1 flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <ArrowUpRight className="h-4 w-4" />
                  <h4 className="text-xs font-bold uppercase tracking-wide">Total Recebido</h4>
                </div>
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                    {formatCurrency(report.totals?.receivedTotal ?? 0)}
                </p>
                  <p className="text-xs text-slate-500">
                    Última atividade: {formatDateTime(report.totals?.lastWorkOrderAt)}
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}

const VehicleBlock = ({
  vehicle,
  onOpenVehicle,
  onOpenWorkOrder,
}: {
  vehicle: ClientReportVehicle;
  onOpenVehicle?: (vehicleId: string) => void;
  onOpenWorkOrder?: (workOrderId: string) => void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-[10px] border border-[#e5e7eb] bg-white dark:border-slate-800 dark:bg-slate-950/50">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => onOpenVehicle?.(vehicle.id)}
          disabled={!onOpenVehicle}
          className="flex min-w-0 flex-1 items-center gap-4 text-left disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8441A]"
          aria-label={onOpenVehicle ? `Abrir veículo ${vehicle.brand} ${vehicle.model}, placa ${formatVehiclePlate(vehicle.plate)}` : undefined}
        >
          <div className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 sm:flex">
            <Car className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className={`text-sm font-bold ${onOpenVehicle ? "text-slate-900 hover:text-[#E8441A] dark:text-white" : "text-slate-900 dark:text-white"}`}>
              {vehicle.brand} {vehicle.model}
              <span className="ml-2 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                {formatVehiclePlate(vehicle.plate)}
              </span>
            </p>
            <div className="flex gap-3 text-xs text-slate-500 dark:text-slate-400"><span>{vehicle.year}</span><span>{vehicle.mileage} km</span></div>
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-4">
          <div className="text-right text-xs"><p className="font-semibold text-slate-700 dark:text-slate-300">{vehicle.workOrders.length} OS</p><p className="text-slate-400">{vehicle.quotes.length} Orç.</p></div>
          <button type="button" onClick={() => setOpen((previous) => !previous)} className="rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8441A]" aria-label={`${open ? "Recolher" : "Expandir"} resumo do veículo`} aria-expanded={open}>
            <ChevronDown className={`h-4 w-4 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {open && (
        <div className="grid gap-3 border-t border-slate-100 bg-slate-50/50 p-3 dark:border-slate-800 dark:bg-slate-900/30 lg:grid-cols-2">
          {/* ORÇAMENTOS */}
          <div className="space-y-2">
            <h5 className="flex items-center gap-2 text-xs font-semibold uppercase text-slate-500">
              <Sheet className="h-3 w-3" /> Orçamentos Recentes
            </h5>
            {vehicle.quotes.length ? (
              <div className="space-y-1">
                {vehicle.quotes.slice(0, 5).map((quote) => (
                  <div key={quote.id} className="flex justify-between rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900">
                    <div>
                      <span className="font-medium text-slate-700 dark:text-slate-200">{quote.number}</span>
                      <span className="mx-1 text-slate-300">•</span>
                      <span className="text-slate-500">{formatDateTime(quote.createdAt)}</span>
                    </div>
                    <div className="font-medium text-[#E8441A] dark:text-[#E8441A]">
                      {formatCurrency(quote.total)}
                    </div>
                  </div>
                ))}
                {vehicle.quotes.length > 5 && (
                  <p className="text-center text-[10px] text-slate-400">
                    + {vehicle.quotes.length - 5} orçamentos
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">Nenhum orçamento.</p>
            )}
          </div>

          {/* OS */}
          <div className="space-y-2">
            <h5 className="flex items-center gap-2 text-xs font-semibold uppercase text-slate-500">
              <Wrench className="h-3 w-3" /> Ordens de Serviço
            </h5>
            {vehicle.workOrders.length ? (
              <div className="space-y-1">
                {vehicle.workOrders.slice(0, 5).map((wo) => (
                  <button
                    key={wo.id}
                    type="button"
                    onClick={() => onOpenWorkOrder?.(wo.id)}
                    disabled={!onOpenWorkOrder}
                    className="flex w-full justify-between rounded-md border border-slate-200 bg-white px-2 py-1.5 text-left text-xs transition hover:border-[#E8441A] disabled:cursor-default disabled:hover:border-slate-200 dark:border-slate-700 dark:bg-slate-900"
                    aria-label={onOpenWorkOrder ? `Abrir ordem de serviço ${wo.number}` : undefined}
                  >
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-slate-700 dark:text-slate-200">{wo.number}</span>
                      <span className={`rounded-sm px-1 py-0.5 text-[9px] font-bold uppercase ${wo.status === WorkOrderStatus.Ready ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {formatWorkOrderStatus(wo.status)}
                      </span>
                    </span>
                    <span className="font-medium text-emerald-700 dark:text-emerald-300">{formatCurrency(wo.total)}</span>
                  </button>
                ))}
                {vehicle.workOrders.length > 5 && (
                  <p className="text-center text-[10px] text-slate-400">
                    + {vehicle.workOrders.length - 5} ordens
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">Nenhuma ordem de serviço.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
